import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import api from "@/api/api";
import type {
  CreateConsultationFormData,
  UpdateConsultationFormData,
} from "@/schemas/consultation";
import type { Consultation } from "@/types/api/consultation";

const createConsultation = async (data: CreateConsultationFormData) => {
  try {
    const res = await api.post<Consultation>(`/consultations`, data);
    return res.data;
  } catch (e) {
    if (isAxiosError(e)) {
      throw new Error(e.response?.data || e.message);
    }
    throw new Error("Error during consultation creation");
  }
};

export const useCreateConsultation = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createConsultation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["consultations", id] });
    },
  });
};

const patchConsultation = (id: number) => {
  return async (data: UpdateConsultationFormData) => {
    try {
      const res = await api.patch<Consultation>(`/consultations/${id}`, data);
      return res.data;
    } catch (e) {
      if (isAxiosError(e)) {
        throw new Error(e.response?.data || e.message);
      }
      throw e;
    }
  };
};

export const usePatchConsultation = (patientId: number, id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchConsultation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["consultations", patientId] });
    },
  });
};

const getConsultationsFromPatient = async (patientId: number) => {
  const { data } = await api.get<Array<Consultation>>(
    `/consultations/patient/${patientId}`
  );
  return data;
};

export const useGetConsultationsFromPatient = (patientId: number) => {
  return useQuery({
    queryKey: ["consultations", patientId],
    queryFn: () => getConsultationsFromPatient(patientId),
    placeholderData: keepPreviousData,
  });
};
